import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWallet, faEye, faEyeSlash, faCommentSms, faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../../AuthContext';
import Modal from '../../components/Modal';
import './AdminSettings.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

function SecretInput({ value, onChange, placeholder, show, onToggle }) {
  return (
    <div className="secret-input-wrap">
      <input
        type={show ? 'text' : 'password'}
        className="form-input secret-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <button type="button" className="secret-toggle" onClick={onToggle}>
        <FontAwesomeIcon icon={show ? faEyeSlash : faEye} />
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const { token } = useAuth();
  const [minDeposit, setMinDeposit] = useState(1000);
  const [whatsapp, setWhatsapp] = useState('');
  const [activeGateway, setActiveGateway] = useState('');
  const [mobilipaKey, setMobilipaKey] = useState('');
  const [sonicpesaKey, setSonicpesaKey] = useState('');
  const [configured, setConfigured] = useState({ mobilipa: false, sonicpesa: false });
  const [textifyKey, setTextifyKey] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [textifyConfigured, setTextifyConfigured] = useState(false);
  const [conversionRate, setConversionRate] = useState(2600);
  const [categories, setCategories] = useState('');
  const [platforms, setPlatforms] = useState('');
  const [showKeys, setShowKeys] = useState({});
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/admin/settings`, { headers })
      .then((r) => r.json())
      .then((d) => {
        if (d.min_deposit) setMinDeposit(Number(d.min_deposit));
        if (d.whatsapp_number !== undefined) setWhatsapp(d.whatsapp_number || '');
        setActiveGateway(d.active_payment_gateway || '');
        setConfigured({ mobilipa: Boolean(d.mobilipa_api_key_set), sonicpesa: Boolean(d.sonicpesa_api_key_set) });
        if (d.conversion_rate) setConversionRate(Number(d.conversion_rate));
        setTextifyConfigured(Boolean(d.textify_api_key_set));
        if (d.admin_phone !== undefined) setAdminPhone(d.admin_phone || '');
      })
      .catch(() => {});
    fetch(`${API}/settings/metadata`)
      .then((r) => r.json())
      .then((d) => {
        if (d.categories) setCategories(d.categories.join(', '));
        if (d.platforms) setPlatforms(d.platforms.join(', '));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      min_deposit: minDeposit,
      whatsapp_number: whatsapp,
      conversion_rate: conversionRate,
      categories: categories,
      platforms: platforms,
      active_payment_gateway: activeGateway || null,
      admin_phone: adminPhone,
    };
    if (mobilipaKey.trim()) payload.mobilipa_api_key = mobilipaKey.trim();
    if (sonicpesaKey.trim()) payload.sonicpesa_api_key = sonicpesaKey.trim();
    if (textifyKey.trim()) payload.textify_api_key = textifyKey.trim();

    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setSaving(false);
      if (!res.ok) {
        const errorMsg = data.errors
          ? Object.values(data.errors).flat().join(' ')
          : data.message || 'Could not save settings.';
        setModal({ open: true, type: 'error', title: 'Save Failed', message: errorMsg });
        return;
      }
      if (mobilipaKey.trim()) setConfigured((current) => ({ ...current, mobilipa: true }));
      if (sonicpesaKey.trim()) setConfigured((current) => ({ ...current, sonicpesa: true }));
      if (textifyKey.trim() || data.textify_api_key_set) setTextifyConfigured(true);
      if (data.admin_phone !== undefined) setAdminPhone(data.admin_phone);
      setMobilipaKey('');
      setSonicpesaKey('');
      setTextifyKey('');
      setModal({ open: true, type: 'success', title: 'Settings Saved', message: 'All settings have been updated successfully.' });
    } catch (err) {
      setSaving(false);
      setModal({ open: true, type: 'error', title: 'Save Failed', message: err.message || 'Network error while saving settings.' });
    }
  };

  const toggleKey = (field) => setShowKeys((prev) => ({ ...prev, [field]: !prev[field] }));

  return (
    <div className="admin-settings">
      <div className="settings-card">
        <h2 className="section-title">
          <FontAwesomeIcon icon={faWallet} style={{ marginRight: 8 }} />
          Deposit Settings
        </h2>

        <form onSubmit={handleSave} className="settings-form">
          <div className="form-group">
            <label className="form-label">Minimum Deposit Amount (TSHS)</label>
            <input
              type="number"
              className="form-input"
              value={minDeposit}
              onChange={(e) => setMinDeposit(Number(e.target.value))}
              min={100}
              required
            />
            <p className="form-hint">Users cannot deposit less than this amount. Default: 1000</p>
          </div>

          <h2 className="section-title" style={{ marginTop: 32 }}>
            <FontAwesomeIcon icon={faWhatsapp} style={{ marginRight: 8 }} />
            WhatsApp Support
          </h2>

          <div className="form-group">
            <label className="form-label">WhatsApp Number</label>
            <input
              type="text"
              className="form-input"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="e.g. 255712345678"
            />
            <p className="form-hint">This number will appear as a floating WhatsApp icon for users to contact support. Include country code without + sign.</p>
          </div>

          <h2 className="section-title" style={{ marginTop: 32 }}>
            Provider Settings
          </h2>

          <div className="form-group">
            <label className="form-label">USD to TSHS Conversion Rate</label>
            <input
              type="number"
              className="form-input"
              value={conversionRate}
              onChange={(e) => setConversionRate(Number(e.target.value))}
              min={1}
              required
            />
            <p className="form-hint">Provider rates in USD will be multiplied by this rate. Default: 2600</p>
          </div>

          <div className="form-group">
            <label className="form-label">Categories</label>
            <input
              type="text"
              className="form-input-wide"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              placeholder="Followers, Likes, Comments, Views, Subscribers, Members, Plays, Shares"
            />
            <p className="form-hint">Comma-separated list of service categories</p>
          </div>

          <div className="form-group">
            <label className="form-label">Platforms</label>
            <input
              type="text"
              className="form-input-wide"
              value={platforms}
              onChange={(e) => setPlatforms(e.target.value)}
              placeholder="Instagram, YouTube, TikTok, Twitter, Telegram, Facebook, Spotify, Discord"
            />
            <p className="form-hint">Comma-separated list of supported platforms</p>
          </div>

          <h2 className="section-title" style={{ marginTop: 32 }}>
            Payment Gateways
          </h2>

          <div className="gateway-card">
            <div className="gateway-header">
              <div className="gateway-info">
                <strong>MobiliPa</strong>
                <span className="gateway-desc">Mobile money payment gateway</span>
              </div>
              <label className="gateway-choice">
                <input type="radio" name="active-gateway" checked={activeGateway === 'mobilipa'} onChange={() => setActiveGateway('mobilipa')} />
                <span>{activeGateway === 'mobilipa' ? 'Active' : 'Use gateway'}</span>
              </label>
            </div>
              <div className="gateway-fields">
                <div className="form-group">
                  <label className="form-label">API Key {configured.mobilipa && <span className="key-configured">(configured)</span>}</label>
                  <SecretInput
                    value={mobilipaKey}
                    onChange={setMobilipaKey}
                    placeholder={configured.mobilipa ? 'Leave blank to keep current key' : 'Enter MobiliPa API key'}
                    show={Boolean(showKeys.mobilipa_key)}
                    onToggle={() => toggleKey('mobilipa_key')}
                  />
                </div>
              </div>
          </div>

          <div className="gateway-card">
            <div className="gateway-header">
              <div className="gateway-info">
                <strong>SonicPesa</strong>
                <span className="gateway-desc">Alternative mobile money gateway</span>
              </div>
              <label className="gateway-choice">
                <input type="radio" name="active-gateway" checked={activeGateway === 'sonicpesa'} onChange={() => setActiveGateway('sonicpesa')} />
                <span>{activeGateway === 'sonicpesa' ? 'Active' : 'Use gateway'}</span>
              </label>
            </div>
              <div className="gateway-fields">
                <div className="form-group">
                  <label className="form-label">API Key {configured.sonicpesa && <span className="key-configured">(configured)</span>}</label>
                  <SecretInput
                    value={sonicpesaKey}
                    onChange={setSonicpesaKey}
                    placeholder={configured.sonicpesa ? 'Leave blank to keep current key' : 'Enter SonicPesa API key'}
                    show={Boolean(showKeys.sonicpesa_key)}
                    onToggle={() => toggleKey('sonicpesa_key')}
                  />
                </div>
              </div>
          </div>

          <h2 className="section-title" style={{ marginTop: 32 }}>
            <FontAwesomeIcon icon={faCommentSms} style={{ marginRight: 8 }} />
            SMS Notifications
          </h2>

          <div className="gateway-card">
            <div className="gateway-header">
              <div className="gateway-info">
                <strong>Textify Africa</strong>
                <span className="gateway-desc">SMS alerts for failed orders &amp; OTP verification</span>
              </div>
              {textifyConfigured && (
                <span className="key-configured sms-active-badge">● Active</span>
              )}
            </div>
            <div className="gateway-fields">
              <div className="form-group">
                <label className="form-label">
                  Textify API Key {textifyConfigured && <span className="key-configured">(configured)</span>}
                </label>
                <SecretInput
                  value={textifyKey}
                  onChange={setTextifyKey}
                  placeholder={textifyConfigured ? 'Leave blank to keep current key' : 'Enter Textify Africa API key'}
                  show={Boolean(showKeys.textify_key)}
                  onToggle={() => toggleKey('textify_key')}
                />
                <p className="form-hint">
                  Get your key at <a href="https://portal.textify.africa" target="_blank" rel="noopener noreferrer">portal.textify.africa</a> → API → Generate Key
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faMobileAlt} style={{ marginRight: 6 }} />
                  Admin Phone Number
                </label>
                <input
                  type="text"
                  className="form-input-wide"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  placeholder="e.g. 255712345678"
                />
                <p className="form-hint">Phone number to receive SMS alerts when an order fails. Include country code without + sign.</p>
              </div>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={saving}>
            <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>

      <Modal open={modal.open} type={modal.type} title={modal.title} message={modal.message} onClose={() => setModal({ ...modal, open: false })} />
    </div>
  );
}
